/**
 * Transport client — the public surface consumers program against.
 *
 * {@link TransportClient} is implemented by both the live WebSocket client
 * ({@link QvWebSocketClient}) and the offline {@link FixtureTransportClient},
 * so the web app can swap between them with no other code changes.
 *
 * The client is deliberately thin and rendering-agnostic: it decodes QV Arrow
 * bundles and emits plain columnar data through listener callbacks. Consumers
 * (e.g. the chart engine) turn {@link ColumnDelta}s into their own `SeriesDelta`.
 */

import { decodeBundle } from "./arrow.js";
import type { GeometryPayload } from "./geometry.js";
import {
  diffChartGroup,
  settledX,
  type AskStamp,
  type ChartGroupColumns,
  type ServerPerf,
  type ColumnDelta,
} from "./columns.js";
import {
  encodeRequest,
  type IndicatorRequest,
  type ChartGroupRequest,
} from "./request.js";
import {
  chartGroupSubscriptionKey,
  type SubscriptionKey,
} from "./subscription.js";

/** Connection lifecycle status. */
export type ConnectionStatus = "idle" | "connecting" | "open" | "closed";

/** A transport-level error surfaced to listeners. */
export interface TransportError {
  /** Where the failure originated. */
  source: "socket" | "decode" | "server";
  message: string;
  /** The offending chart group id, when the error is server-reported per group. */
  chartGroupId?: string;
  cause?: unknown;
}

/**
 * What the transport spent getting this frame to the consumer.
 *
 * Carried on the payload that is already correlated by `chartGroupId` rather
 * than through a listener of its own: a second callback would be a second thing
 * for the hub to route, for facts that belong to the frame it is routing.
 *
 * Every field is bundle-level except {@link diffMs} — one socket carries one
 * request per *layout*, so the queue, the wire and the decode are paid once for
 * every chart in it and attributing them to one chart would multiply them.
 */
export interface TransportTiming {
  /** `ws.send` to the first byte back. **Contains the server's whole build.** */
  wireMs: number;
  /** `decodeBundle` — envelope framing plus Arrow IPC to plain arrays. */
  decodeMs: number;
  /** `diffChartGroup` for this group. Zero on a bulk load, which does not diff. */
  diffMs: number;
  /** Bytes on the wire, whole bundle. */
  bytes: number;
  /** How many chart groups shared this response — what `wireMs` was paid for. */
  groupCount: number;
}

/** Initial (bulk) load of one chart group's full column set. */
export interface BulkLoad {
  subscriptionKey: SubscriptionKey;
  chartGroupId: string;
  columns: ChartGroupColumns;
  /** Absent on a frame the client did not time — the fixture transport. */
  timing?: TransportTiming;
}

/** A realtime append/patch frame for one chart group, as per-alias deltas. */
export interface AppendFrame {
  subscriptionKey: SubscriptionKey;
  chartGroupId: string;
  deltas: ColumnDelta[];
  timing?: TransportTiming;
  /**
   * The server's timings for the snapshot this diff came from.
   *
   * Carried here as well as on `BulkLoad.columns` because an append hands back
   * deltas rather than the snapshot — and the refresh path is exactly where the
   * `source: "refresh"` label matters, since those timings describe a rebuild
   * the server did once for every subscriber.
   */
  serverPerf?: ServerPerf;
}

/** A refresh that does not meet the bars held — see {@link TransportListeners.onGap}. */
export interface RefreshGap {
  subscriptionKey: string;
  chartGroupId: string;
  /** The newest settled bar the client holds, in the group's x units. */
  heldThrough: number;
  /** The oldest bar the refresh carried — past `heldThrough`, which is the gap. */
  firstSent: number;
}

/** An intraday chart's live state, as the server said it on the latest frame. */
export interface LiveInfo {
  chartGroupId: string;
  todaySource?: "intra" | "rest";
  phase?: "live" | "closed";
  /** The last pre- or post-market price, and which; absent in the session. */
  extPrice?: number;
  extPhase?: "pre" | "post";
  /** When this frame arrived, `Date.now()` — how a consumer tells a stalled feed. */
  receivedAt: number;
}

/** The shapes on one chart group's latest frame — `qf.geometry`, decoded. */
export interface GeometryFrame {
  subscriptionKey: SubscriptionKey;
  chartGroupId: string;
  geometry: GeometryPayload;
}

/** Callback bundle registered on a client. All handlers are optional. */
export interface TransportListeners {
  /**
   * Every frame of an intraday chart — the load and each refresh, **whether or
   * not it carried a new bar** — with the server's live facts. `onAppend` is
   * silent when a refresh changes nothing, and a live indicator driven by it
   * would go grey on a quiet minute.
   */
  onLive?(info: LiveInfo): void;
  /**
   * Every frame — load or refresh — that carried shapes. **A replacement, not
   * a diff**: each one is the detectors' whole answer for that group, so the
   * consumer keeps only the latest. Separate from `onBulkLoad`/`onAppend`
   * because those are about bars, and a refresh that moved no bar can still
   * carry a line that broke.
   */
  onGeometry?(frame: GeometryFrame): void;
  /** Fired once per chart group when its first snapshot arrives. */
  onBulkLoad?(load: BulkLoad): void;
  /** Fired for each subsequent snapshot, carrying the diff against the last one. */
  onAppend?(frame: AppendFrame): void;
  /**
   * A refresh's bars do not reach back to the last bar held, so the bars in
   * between are missing — and appending would draw a line straight through them.
   *
   * Nothing is appended. The consumer is expected to **rebuild the chart**: a
   * bulk load onto a surface that is already drawn appends the whole history on
   * top of itself, so re-requesting from here would be worse than the gap. The
   * client keeps ignoring this group's refreshes until a load for it arrives.
   * With no handler the gap is reported through {@link onError} instead.
   */
  onGap?(gap: RefreshGap): void;
  /** Fired on socket / decode / server errors. */
  onError?(error: TransportError): void;
  /**
   * Fired on connection status transitions.
   *
   * **Wire it.** A dropped socket fires none of the three callbacks above, so a
   * consumer that skips this one goes on showing the bars it last received for
   * as long as the feed is down — which on a price chart is indistinguishable
   * from a quiet market. The client reconnects underneath (see
   * {@link QvWebSocketClient}), but the gap is still a gap and only the view can
   * say so.
   */
  onStatus?(status: ConnectionStatus): void;
}

export interface RequestOptions {
  /**
   * Groups whose consumer has **nothing drawn** — a chart rebuilt for a new
   * symbol, interval or plot — and so must be answered with a bulk load rather
   * than a diff against the snapshot the client still holds for that id.
   *
   * Named by the caller because only the caller knows. The request cannot say:
   * one socket carries a whole layout, so the same id arrives again for a chart
   * that merely sat beside the one that changed, and a bulk load onto *its*
   * surface draws every bar twice. Nor can the subscription key: a rebuild for
   * `barsRange` or the x axis changes nothing in it.
   */
  reload?: ReadonlyArray<string | number>;
}

/** The common client contract (live and fixture share it). */
export interface TransportClient {
  /** Open the connection (resolves when ready to accept requests). */
  connect(): Promise<void>;
  /** Send an indicator request; results arrive via the registered listeners. */
  request(req: IndicatorRequest, options?: RequestOptions): Promise<void>;
  /** Close the connection and release resources. */
  disconnect(): void;
  /** Current lifecycle status. */
  readonly status: ConnectionStatus;
}

// ── Injectable WebSocket seam (keeps the client unit-testable) ────────────────

/** The minimal subset of the browser `WebSocket` the client depends on. */
export interface WebSocketLike {
  binaryType: string;
  send(data: string | ArrayBufferLike | ArrayBufferView): void;
  close(code?: number, reason?: string): void;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: unknown) => void) | null;
  onerror: ((ev: unknown) => void) | null;
}

/** Factory that mints a {@link WebSocketLike}; defaults to the global `WebSocket`. */
export type WebSocketFactory = (
  url: string,
  protocols?: string | string[],
) => WebSocketLike;

/** Options for {@link QvWebSocketClient}. */
export interface QvWebSocketClientOptions {
  /** Full WebSocket URL of the QV endpoint, e.g. `"wss://qv-data.fly.dev/ws/qv"`. */
  url: string;

  /** Listener callbacks. */
  listeners?: TransportListeners;

  /**
   * Optional Firebase ID token; appended as an `access_token` query parameter on
   * connect. Firebase itself is out of scope for transport.
   */
  idToken?: string;

  /** Inject a WebSocket implementation (defaults to the global `WebSocket`). */
  webSocketFactory?: WebSocketFactory;
  /**
   * The Firebase ID token to open with, if this deployment authenticates.
   *
   * Called on every open — including a reconnect — rather than read once,
   * because a token expires in an hour and a socket that drops overnight would
   * otherwise reconnect with a dead one and be closed immediately, forever.
   *
   * Injected rather than imported: this package is rendering- and
   * vendor-agnostic (D18 hosts it in four WebViews), and reaching for Firebase
   * here would make it unusable in most of them. Return `null` when there is no
   * token and no frame is sent, which is the unauthenticated path unchanged.
   */
  authToken?: () => string | null | Promise<string | null>;
  /**
   * Who the stream is *for*, when that differs from who is asking.
   *
   * Travels in the same opening frame as the token, because a socket that
   * authenticated as one account and streamed another's would be the worst
   * possible split. Named generically: see `authToken` on why this package
   * knows nothing about impersonation.
   */
  actAs?: () => string | null | undefined;
  /**
   * The signed groups pass from `/auth/me`, sent in the same opening frame.
   *
   * Lets the server decide what this caller may see without asking its
   * database (`docs/plans/auth-hot-path.md`). Optional: without it the server
   * resolves the caller from Postgres, exactly as before.
   */
  pass?: () => string | null | undefined;

  /** Override request encoding (defaults to gzip'd JSON via {@link encodeRequest}). */
  encodeRequest?: (req: IndicatorRequest) => Promise<Uint8Array> | Uint8Array;
}

// ── Reconnect schedule ────────────────────────────────────────────────────────

/** First retry delay; doubles per consecutive failure. */
const RECONNECT_BASE_MS = 500;

/**
 * Delay ceiling. The server re-pushes every `QV_CHART_REFRESH_SECONDS` (60 by
 * default), so a client back inside half a refresh period has missed nothing the
 * next push would not have carried anyway. Waiting longer than that buys the
 * server no relief it has not already had and leaves stale prices on screen for
 * a whole cycle, which is the failure this whole mechanism exists for.
 */
const RECONNECT_CAP_MS = 30_000;

/**
 * How long to wait before retry number `attempt` (0-based).
 *
 * **The jitter is not decoration.** One layout is several charts and each chart
 * is its own socket, so a qv-data restart drops them all in the same
 * millisecond; an undithered schedule marches every one of them back in
 * lockstep — the herd that arrives together on a server that has only just
 * finished dying. Several browsers make it worse in exactly the same phase.
 *
 * It is subtractive — a delay in `[d/2, d]` rather than the textbook full
 * `[0, d]` — for two reasons. The cap stays a real ceiling, and the schedule
 * never produces a near-instant retry, which is the one attempt guaranteed to
 * land on a server that is still starting up.
 */
function reconnectDelay(attempt: number): number {
  const ceiling = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_CAP_MS);
  return ceiling / 2 + Math.random() * (ceiling / 2);
}

function defaultWebSocketFactory(
  url: string,
  protocols?: string | string[],
): WebSocketLike {
  const Ctor = (globalThis as { WebSocket?: unknown }).WebSocket;
  if (typeof Ctor !== "function") {
    throw new Error(
      "No global WebSocket available; pass options.webSocketFactory.",
    );
  }
  return new (Ctor as new (u: string, p?: string | string[]) => WebSocketLike)(
    url,
    protocols,
  );
}

/**
 * Live client for QV's `/ws/qv`. Sends gzip'd JSON requests, receives binary
 * Arrow bundles, decodes them, and emits a bulk load then diff-based append
 * frames per chart group. The WebSocket is injectable so this is fully testable
 * without a real server.
 *
 * **A dropped socket is reconnected and the last request replayed**, on the
 * backoff above. Without it the chart simply stopped: a close fired no listener
 * the consumer registers, so a qv-data restart mid-session left every chart
 * drawing the last bars it had received — the previous day's close, unmarked,
 * beside a server that was serving today's. Stale prices that look live are the
 * one failure a trading chart must not have, which is why the recovery is here
 * and the *status* reaches the view as well.
 */
export class QvWebSocketClient implements TransportClient {
  private readonly options: QvWebSocketClientOptions;
  private ws: WebSocketLike | null = null;
  private _status: ConnectionStatus = "idle";

  /** chartGroupId → subscription key, learned from the most recent request. */
  private readonly subKeyByGroup = new Map<string, SubscriptionKey>();

  /**
   * chartGroupId → what the most recent request asked for: symbol, interval
   * and aliases. A frame stamped for anything else is the answer to a request
   * this one replaced, and is dropped in `dispatchGroup`.
   */
  private readonly askByGroup = new Map<string, Required<AskStamp>>();

  /** chartGroupId → last decoded snapshot, for realtime diffing. */
  private readonly lastByGroup = new Map<string, ChartGroupColumns>();

  /**
   * Groups to forget from `lastByGroup` at the next send — see
   * {@link RequestOptions.reload}. Held until a request actually goes out
   * rather than applied on the call, because one made while the socket is down
   * goes out as the replay.
   */
  private readonly pendingReload = new Set<string>();

  /**
   * Groups whose refreshes stopped meeting the bars held, reported once through
   * `onGap` and ignored until a load for the group replaces what is held. Every
   * later tail would fail the same check, and a consumer told once a minute is
   * a consumer rebuilding once a minute.
   */
  private readonly gapped = new Set<string>();

  /**
   * When the last request went out, for the wire span.
   *
   * One stamp rather than a map keyed by request, because there is nothing to
   * key it by: this protocol has no request id and correlates purely by
   * `chartGroupId` — and one socket carries one request per layout at a time,
   * so the most recent send is the one every arriving bundle answers. It is
   * also what makes the 60-second refresh honest: a push nobody asked for is
   * measured from the last thing that *was* asked for, which is why the server
   * labels a refresh and the panel reads that label rather than this number.
   */
  private sentAt: number | null = null;

  /** The bundle-level spans for the frame being dispatched. Null between frames. */
  private timing: TransportTiming | null = null;

  /**
   * The most recent request, replayed on every reconnect — and the flag that
   * says a reconnect is wanted at all. See {@link request}.
   */
  private lastRequest: IndicatorRequest | null = null;

  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;

  /** True from `disconnect()` until the next `connect()`; suppresses retries. */
  private closedByCaller = false;

  /** True from a reconnect's replayed request until the bundle answering it. */
  private resuming = false;

  constructor(options: QvWebSocketClientOptions) {
    this.options = options;
  }

  get status(): ConnectionStatus {
    return this._status;
  }

  connect(): Promise<void> {
    if (this.ws && (this._status === "open" || this._status === "connecting")) {
      return Promise.resolve();
    }
    // A caller reconnecting by hand supersedes whatever we had scheduled, and
    // starts the backoff over: this attempt is not the failed one's successor.
    this.closedByCaller = false;
    this.reconnectAttempt = 0;
    this.cancelReconnect();
    return this.openSocket();
  }

  async request(req: IndicatorRequest, options: RequestOptions = {}): Promise<void> {
    for (const id of options.reload ?? []) this.pendingReload.add(String(id));
    const open = this.ws !== null && this._status === "open";
    // **Queued as the replay while a dropped socket is coming back**, rather than
    // refused. It used to throw here before recording anything, so the reconnect
    // replayed whatever the socket last carried: a symbol changed during an
    // outage came back as the old symbol, drawn onto the new chart. Only while a
    // replay is armed — before the first request there is nothing to resume, and
    // a request queued for a send that will never happen is a silent failure.
    if (!open && (!this.lastRequest || this.closedByCaller)) {
      throw new Error("request() called before the socket is open");
    }
    // Remember chartGroupId → subscription key for correlating responses.
    for (const cg of req.chartGroups) {
      this.subKeyByGroup.set(
        String(cg.chartGroupId),
        chartGroupSubscriptionKey(cg),
      );
      this.askByGroup.set(String(cg.chartGroupId), askOf(cg));
    }
    // Held for the same reason and at the same moment, because it is the same
    // knowledge: the request is the only thing that tells the server which
    // symbol, frequency, bar count and aliases this *connection* wants, and the
    // server keys its refresh channel off it (`ChartManager` in
    // `services/qv-data/app/routers/qv.py`). A reconnected socket that does not
    // replay it is silent forever — the same frozen chart as before the fix,
    // now with the connection reading as healthy.
    //
    // It is also what **arms** the reconnect. A socket that never carried a
    // request has nothing to resume, so a drop before the first `request()` is
    // left closed for the caller's own `connect()` to answer for rather than
    // retried into a green light over a server that will never send anything.
    //
    // Recorded before the encode, not after the send: a socket that dies
    // mid-encode is precisely a socket whose request should be replayed.
    this.lastRequest = req;
    if (!open) return;

    const encode = this.options.encodeRequest ?? ((r) => encodeRequest(r));
    const payload = await encode(req);

    // Re-checked, because the guard above does not survive the await. Encoding
    // is asynchronous, and `disconnect()` during it leaves `this.ws` null — which
    // React's StrictMode double-mount reproduces on every dev page load: connect,
    // request, unmount, disconnect, remount. Without this the failure surfaces as
    // `Cannot read properties of null (reading 'send')` from inside the transport
    // rather than as the condition this method actually documents.
    if (!this.ws || this._status !== "open") {
      throw new Error("socket closed while the request was being encoded");
    }
    // Forgotten at the send and not before it, so the next bundle carrying the
    // group is this request's answer and arrives as a bulk load. Earlier, and a
    // 60s refresh of the *previous* request landing during the encode would be
    // taken for it. A push already in flight when the server reads this one can
    // still be; the protocol has no request id to tell them apart.
    for (const id of this.pendingReload) this.lastByGroup.delete(id);
    this.pendingReload.clear();
    // Stamped either side of `send` rather than before the encode: the gzip is
    // the client's own cost and belongs to no stage on the wire.
    this.sentAt = now();
    this.ws.send(payload);
  }

  disconnect(): void {
    // Both of these run **before** `close()`, because a `WebSocketLike` may
    // deliver `onclose` synchronously (the test double does, and a browser may
    // for a socket still handshaking) and that handler must already know this
    // close was ours. A reconnect timer that outlives a disconnect resurrects a
    // client its owner has thrown away — and `disconnect()` runs on every
    // unmount *and* before every rebuild, so a leak here is not a corner case:
    // it is one dead socket per symbol change, all of them still decoding.
    this.closedByCaller = true;
    this.cancelReconnect();

    if (this.ws) {
      const ws = this.ws;
      this.ws = null;
      ws.close();
    }
    this.subKeyByGroup.clear();
    this.askByGroup.clear();
    this.lastRequest = null;
    this.resuming = false;
    // Dropped with the request it measures. A socket reopened later would
    // otherwise report a wire span reaching back to the previous chart.
    this.sentAt = null;
    // Dropped here and deliberately **kept** across a reconnect — see
    // `dispatchGroup`. A disconnect is the consumer letting its chart go; a
    // reconnect is the same chart, still drawn, waiting for the rest of its bars.
    this.lastByGroup.clear();
    this.gapped.clear();
    this.setStatus("closed");
  }

  // ── internals ───────────────────────────────────────────────────────────

  /** Open one socket and wire its handlers; shared by `connect` and the retry. */
  private openSocket(): Promise<void> {
    this.setStatus("connecting");

    const factory = this.options.webSocketFactory ?? defaultWebSocketFactory;
    const ws = factory(this.buildUrl());
    ws.binaryType = "arraybuffer";
    this.ws = ws;

    return new Promise<void>((resolve, reject) => {

      ws.onopen = () => {
        // **The auth frame goes first, before anything else is sent.**
        //
        // A browser cannot set `Authorization` on a WebSocket handshake, so
        // qv-data reads the token from the opening message and closes the
        // socket (4401) if it does not arrive. Sent as *text*, which is what
        // distinguishes it from every other frame on this socket — those are
        // binary.
        //
        // `authToken` is injected rather than imported: this package is
        // rendering- and vendor-agnostic, and pulling Firebase in here would
        // make it unusable in the WebView hosts it exists to serve.
        void (async () => {
          try {
            const token = await this.options.authToken?.();
            if (token) {
              const actAs = this.options.actAs?.() || undefined;
              const pass = this.options.pass?.() || undefined;
              ws.send(
                JSON.stringify({
                  type: "auth",
                  token,
                  ...(actAs ? { actAs } : {}),
                  ...(pass ? { pass } : {}),
                }),
              );
            }
          } catch {
            // A token we could not mint is the same as none: the server will
            // close the socket, and the reconnect path handles it. Throwing
            // here would take out `onopen` and leave the socket half-alive.
          }
          this.reconnectAttempt = 0;
          this.setStatus("open");
          resolve();
        // A socket that opens with a request already behind it is a replacement
        // for one that dropped, and the server remembers nothing about a client
        // — it keys its channel off the request. Nobody else will re-send it:
        // the consumer wired three data callbacks and never hears about the
        // close at all.
          if (this.lastRequest) void this.resume();
        })();
      };
      ws.onmessage = (ev) => {
        void this.handleMessage(ev.data);
      };
      ws.onclose = () => {
        // Only the socket we are still holding may declare the feed dead. A
        // superseded one closing — a `disconnect()` mid-handshake, or a browser
        // delivering the close of a socket we have already replaced — would
        // otherwise null out the live socket and take the chart down with it.
        if (this.ws !== ws) return;
        this.ws = null;
        this.setStatus("closed");
        this.scheduleReconnect();
      };
      ws.onerror = (cause) => {
        this.emitError({ source: "socket", message: "WebSocket error", cause });
        if (this._status === "connecting") reject(new Error("WebSocket error"));
      };
    });
  }

  /**
   * Wait out the backoff, then try again.
   *
   * Guarded on a timer already being pending because `onclose` and a throwing
   * factory can both reach here for one failure, and two live timers would
   * halve the delay every time they raced.
   */
  private scheduleReconnect(): void {
    if (this.closedByCaller || !this.lastRequest) return;
    if (this.reconnectTimer !== null) return;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.closedByCaller) return;
      try {
        void this.openSocket().catch(() => {
          // A refused handshake announces itself through `onerror` and then
          // `onclose`, and the close schedules the next attempt. This catch
          // exists only so the rejection is not an unhandled one.
        });
      } catch (cause) {
        // The factory itself threw, so there is no socket to deliver an
        // `onclose` and the retry chain would end here, silently.
        this.emitError({ source: "socket", message: "reconnect failed", cause });
        this.scheduleReconnect();
      }
    }, reconnectDelay(this.reconnectAttempt++));
  }

  private cancelReconnect(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /** Re-subscribe a socket that replaced a dropped one. */
  private async resume(): Promise<void> {
    const req = this.lastRequest;
    if (!req) return;
    // Set before the replay rather than after it: the next bundle to arrive is
    // this request's answer, and `dispatchGroup` has to know that this one
    // snapshot may not be continuous with the last one it saw.
    this.resuming = true;
    try {
      await this.request(req);
    } catch (cause) {
      this.emitError({
        source: "socket",
        message: "failed to resume the subscription",
        cause,
      });
    }
  }

  private buildUrl(): string {
    const { url, idToken } = this.options;
    if (!idToken) return url;
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}access_token=${encodeURIComponent(idToken)}`;
  }

  private async handleMessage(data: unknown): Promise<void> {
    // **Taken first, before the payload is even coerced.** A response that
    // arrives is the end of the wire span whatever shape it turns out to be in,
    // and stamping after the gunzip-sniff would quietly bill decompression to
    // the network.
    const arrivedAt = now();
    try {
      const bytes = await toBytes(data);
      if (!bytes) return;
      const decodeStarted = now();
      const bundle = decodeBundle(bytes);
      const decodeMs = now() - decodeStarted;
      // `sentAt` is null when the socket delivers something we never asked for
      // — a push after a resume the caller cancelled — and a wire span measured
      // from nothing would be the age of the page.
      const wireMs = this.sentAt === null ? 0 : arrivedAt - this.sentAt;
      this.timing = {
        wireMs,
        decodeMs,
        diffMs: 0,
        bytes: bytes.byteLength,
        groupCount: bundle.groups.length,
      };
      for (const group of bundle.groups) {
        this.dispatchGroup(group);
      }
      // One bundle answers the whole replayed request, so a resume ends with the
      // bundle rather than with each group inside it.
      this.resuming = false;
    } catch (cause) {
      this.emitError({ source: "decode", message: "Failed to decode bundle", cause });
    } finally {
      this.timing = null;
    }
  }

  private dispatchGroup(group: ChartGroupColumns): void {
    const key = this.subKeyByGroup.get(group.chartGroupId) ?? group.chartGroupId;

    // **A frame for an ask this group no longer makes is dropped first**,
    // before even the live facts. The `reload` above forgets the snapshot at the
    // send, so the next whole load is taken as the answer — and with two asks in
    // flight that was the *previous* one: JPM's 14,528 bars drawn under a META
    // title, then META's load diffed onto them as one patched last bar
    // (2026-10-16). The tail tag cannot catch it; both were loads. An interval
    // or an indicator changed as fast is the same race, so all three are held.
    const wanted = this.askByGroup.get(group.chartGroupId);
    if (wanted && group.answers && !answersAsk(group.answers, wanted)) return;

    if (group.todaySource !== undefined || group.phase !== undefined || group.extPrice !== undefined) {
      this.options.listeners?.onLive?.({
        chartGroupId: group.chartGroupId,
        todaySource: group.todaySource,
        phase: group.phase,
        extPrice: group.extPrice,
        extPhase: group.extPhase,
        receivedAt: Date.now(),
      });
    }

    if (group.status === "error") {
      this.emitError({
        source: "server",
        message: group.error ?? "server error",
        chartGroupId: group.chartGroupId,
      });
      return;
    }

    const prev = this.lastByGroup.get(group.chartGroupId);

    // **A tail is never a load, and must meet what is held.** The server sends
    // a refresh as the last two bars (`CHART_REFRESH_BARS`), tagged. With
    // nothing held — the snapshot was dropped for a rebuild's answer, which is
    // still on its way — it is somebody else's refresh landing early, and drawn
    // as a load it would be two bars of chart. With something held, its first
    // bar has to be one the client already has: two bars carry the one before
    // the live one precisely so that "the next bar" and "three bars later" can
    // be told apart without a trading calendar. When they cannot be, nothing is
    // appended and the consumer is told to rebuild.
    if (group.tail) {
      if (!prev || this.gapped.has(group.chartGroupId)) return;
      const heldThrough = settledX(prev);
      const firstSent = group.x[0];
      if (heldThrough !== undefined && firstSent !== undefined && firstSent > heldThrough) {
        this.gapped.add(group.chartGroupId);
        const gap = { subscriptionKey: key, chartGroupId: group.chartGroupId, heldThrough, firstSent };
        if (this.options.listeners?.onGap) {
          this.options.listeners.onGap(gap);
        } else {
          this.emitError({
            source: "server",
            message: "bars were missed since the chart loaded — reload the chart to refill it",
            chartGroupId: group.chartGroupId,
          });
        }
        return;
      }
    }

    // **Only a frame that is accepted may carry shapes.** A tail dropped above
    // can be the *previous* symbol's refresh landing ahead of the new load —
    // its lines sit at the same dates with another stock's prices, and drawn
    // they would be confidently wrong.
    if (group.geometry) {
      this.options.listeners?.onGeometry?.({
        subscriptionKey: key,
        chartGroupId: group.chartGroupId,
        geometry: group.geometry,
      });
    }

    if (!prev) {
      this.gapped.delete(group.chartGroupId);
      this.lastByGroup.set(group.chartGroupId, group);
      this.options.listeners?.onBulkLoad?.({
        subscriptionKey: key,
        chartGroupId: group.chartGroupId,
        columns: group,
        // `diffMs: 0` and not omitted: a bulk load genuinely does not diff, and
        // a stage that did not run reads better as zero-with-a-reason than as a
        // row that is simply not there.
        timing: this.timing ?? undefined,
      });
      return;
    }

    // **A reconnect keeps `lastByGroup` and resumes into a diff, never a second
    // bulk load**, and the reason is on the consumer's side of the callback:
    // `onBulkLoad` is not a replace. The web app maps it through `bulkDeltas`
    // into `ChartBuilder.updateSeries`, whose `append` is a plain
    // `DataSeries.append` per point, so a bulk load after a drop draws the whole
    // history a second time on top of itself — duplicate x values, a doubled
    // candle count and a chart that renders perfectly. Emitting one would need a
    // "clear this group" callback that does not exist and would cost every
    // consumer a reset path.
    //
    // The diff costs nothing the ordinary 60s refresh does not already cost:
    // `diffChartGroup` appends the rows past the last x held, which is exactly
    // the bars the outage missed, and patches the bar that moved while we were
    // away.
    //
    // The one thing it cannot express is an outage longer than the loaded
    // window, where the server's oldest bar is newer than the newest one held
    // and the two ranges never meet. Appending then leaves a hole in the middle
    // of the series — absent bars, drawn as a continuous line, which is this
    // codebase's least favourite kind of wrong — so it is reported rather than
    // smoothed over. The append still goes out: current prices with a warning
    // beat correct-looking stale ones.
    if (this.resuming) {
      const newestHeld = prev.x[prev.x.length - 1];
      const oldestSent = group.x[0];
      if (
        newestHeld !== undefined &&
        oldestSent !== undefined &&
        oldestSent > newestHeld
      ) {
        this.emitError({
          source: "socket",
          message:
            "reconnected past the loaded history — the bars during the outage are missing; reload the chart to refill it",
          chartGroupId: group.chartGroupId,
        });
      }
    }

    const diffStarted = now();
    const deltas = diffChartGroup(prev, group);
    const diffMs = now() - diffStarted;
    this.lastByGroup.set(group.chartGroupId, group);
    if (deltas.length > 0) {
      this.options.listeners?.onAppend?.({
        subscriptionKey: key,
        chartGroupId: group.chartGroupId,
        deltas,
        // Per group, unlike the spans above: the diff is the one thing on this
        // path paid once per chart rather than once per bundle.
        timing: this.timing ? { ...this.timing, diffMs } : undefined,
        serverPerf: group.serverPerf,
      });
    }
  }

  private emitError(error: TransportError): void {
    this.options.listeners?.onError?.(error);
  }

  private setStatus(status: ConnectionStatus): void {
    if (this._status === status) return;
    this._status = status;
    this.options.listeners?.onStatus?.(status);
  }
}

/** Coerce a WebSocket message payload to `Uint8Array`, gunzipping if needed. */
/**
 * A monotonic millisecond clock, falling back to `Date.now` where there is none.
 *
 * `performance.now` is the right one — it cannot go backwards when the system
 * clock is adjusted, which `Date.now` can, and a negative stage duration in a
 * timing panel reads as a bug in the panel. The fallback exists because this
 * package runs in four hosts (browser, jsdom, Node, a WebView) and a transport
 * that throws on a missing global would fail the chart, not the measurement.
 */
function now(): number {
  return typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();
}

async function toBytes(data: unknown): Promise<Uint8Array | null> {
  let bytes: Uint8Array | null = null;
  if (data instanceof Uint8Array) bytes = data;
  else if (data instanceof ArrayBuffer) bytes = new Uint8Array(data);
  else if (ArrayBuffer.isView(data)) {
    const view = data as ArrayBufferView;
    bytes = new Uint8Array(view.buffer, view.byteOffset, view.byteLength);
  } else return null;

  // Detect a gzip stream (QV may optionally gzip the response frame).
  if (bytes.length >= 2 && bytes[0] === 0x1f && bytes[1] === 0x8b) {
    return gunzip(bytes);
  }
  return bytes;
}

/** Gunzip via the platform `DecompressionStream`; passthrough when absent. */
async function gunzip(bytes: Uint8Array): Promise<Uint8Array> {
  const DS = (globalThis as { DecompressionStream?: unknown }).DecompressionStream;
  if (typeof DS !== "function") return bytes;
  const stream = new Blob([bytes as BlobPart])
    .stream()
    .pipeThrough(new (DS as typeof DecompressionStream)("gzip"));
  const buf = await new Response(stream).arrayBuffer();
  return new Uint8Array(buf);
}

export type { ChartGroupRequest };

/**
 * What one chart group asks for, spelled as the server stamps it
 * (`answers_metadata` in `routers/qv.py`): the symbol upper-cased, the interval
 * as sent, the aliases as a sorted set — the request's own `Aliases`, not the
 * subscription key's shortname fallback, because those are what go on the wire.
 */
function askOf(cg: IndicatorRequest["chartGroups"][number]): Required<AskStamp> {
  return {
    symbol: cg.symbol.trim().toUpperCase(),
    frequency: (cg.frequency ?? "").trim(),
    aliases: [...new Set(cg.indicators.flatMap((ind) => ind.aliases ?? []))].sort(),
  };
}

/** Whether a stamp matches the ask; a part the server did not send is not checked. */
function answersAsk(stamp: AskStamp, ask: Required<AskStamp>): boolean {
  if (stamp.symbol !== undefined && stamp.symbol.trim().toUpperCase() !== ask.symbol) return false;
  if (stamp.frequency !== undefined && stamp.frequency.trim() !== ask.frequency) return false;
  if (stamp.aliases !== undefined) {
    const sent = [...new Set(stamp.aliases)].sort();
    if (sent.length !== ask.aliases.length || sent.some((a, i) => a !== ask.aliases[i])) return false;
  }
  return true;
}
